// `PostHogInterface`, not `PostHog`: it is what the `loaded` callback receives,
// and the `posthog` singleton the other call sites use satisfies it too.
import type { PostHogInterface } from "posthog-js";

/**
 * Super properties stamped on every client-side event, autocapture event and
 * flag exposure.
 *
 * `platform` is what makes mobile-vs-web answerable across the whole project:
 * the backend derives its own `platform` but only stamps it on the ten events
 * in `POSTHOG_MIRRORED_EVENTS` (ezspot-api), so nothing lands it on a pageview,
 * autocapture event or flag exposure. The literal `"web"`, not something
 * derived from the browser — a mobile *browser* is `web` here, matching the
 * backend's `AnalyticsPlatform` union, and `$device_type` already answers the
 * form-factor question.
 *
 * Named to match the super property `ezspot-mobile#157` adds, so one breakdown
 * answers the question on both clients. That PR is still open: until it lands,
 * a `platform` breakdown reads as web-only rather than as web-vs-mobile.
 */
const SUPER_PROPERTIES = { platform: "web" } as const;

/**
 * Applies {@link SUPER_PROPERTIES}, and must be called again after ANY consent
 * or identity transition that resets the instance.
 *
 * posthog-js keeps super properties in the persisted property store, and
 * `reset()` clears it. Every path that crosses the cookieless boundary or drops
 * the identity reaches a reset, and none of them reload the page, so without a
 * re-register every event after them is untagged for the rest of the session:
 *
 *   - declining cookies — under `cookieless_mode: "on_reject"`,
 *     `opt_out_capturing()` calls `reset(true)` when the visitor was opted in,
 *     then restores only `distinct_id` and `$device_id`
 *   - accepting cookies — `opt_in_capturing()` resets only while
 *     `consent.isRejected()`, so in practice only for a visitor who already
 *     declined
 *   - signing out — a bare `posthog.reset()`
 *   - the initial `opt_in_capturing()` in `loaded`, which resets out of the
 *     cookieless sentinel state, which is why registration happens after it
 *
 * Call it after every one of them rather than reasoning about which can reset
 * in practice: it is idempotent, and the alternative is an invariant about two
 * separate stores staying in sync that a later change could quietly break.
 *
 * This mirrors `resetAnalytics()` in `ezspot-mobile/src/lib/analytics.ts`.
 */
export function registerSuperProperties(client: PostHogInterface): void {
  client.register(SUPER_PROPERTIES);
}
